import { useCallback } from "react";

import {
	useCreatePinnedTabMutation,
	useDeletePinnedTabMutation,
	useGetPinnedTabsQuery,
	useUpdatePinnedTabMutation,
} from "@/pages/Private/redux/listOverviewPinnedTab/listOverviewPinnedTab.api";

import { PinnedFilterTab, PinnedTabFilters } from "../utils/pinnedFilterTabs";

/**
 * Per-user pinned Lead List overview filter tabs, persisted on the backend
 * (`/list-overview-pinned-tabs`) so they sync across devices. Wraps the RTK Query endpoints in a
 * small CRUD surface; the mutating calls return promises so callers can react (toast) on
 * success/failure.
 */
export const useListOverviewPinnedTabs = () => {
	const { data } = useGetPinnedTabsQuery();
	const [createPinnedTab] = useCreatePinnedTabMutation();
	const [updatePinnedTab] = useUpdatePinnedTabMutation();
	const [deletePinnedTab] = useDeletePinnedTabMutation();

	const pinnedTabs: PinnedFilterTab[] = data?.data ?? [];

	const addPinnedTab = useCallback(
		(name: string, filters: PinnedTabFilters) => createPinnedTab({ name, filters }).unwrap(),
		[createPinnedTab]
	);

	const renamePinnedTab = useCallback(
		(id: number, name: string) => updatePinnedTab({ id, name }).unwrap(),
		[updatePinnedTab]
	);

	const updatePinnedTabFilters = useCallback(
		(id: number, filters: PinnedTabFilters) => updatePinnedTab({ id, filters }).unwrap(),
		[updatePinnedTab]
	);

	const removePinnedTab = useCallback(
		(id: number) => deletePinnedTab(id).unwrap(),
		[deletePinnedTab]
	);

	return {
		pinnedTabs,
		addPinnedTab,
		renamePinnedTab,
		updatePinnedTabFilters,
		removePinnedTab,
	};
};
